import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FlightOfferStatus,
  type FlightOfferStatus as FlightOfferStatusEnum,
} from '@wingseat/shared';
import {
  useAdminFlights,
  useDeactivateFlight,
  useHideFlight,
  useUnhideFlight,
} from '../../hooks/use-admin';
import { ApiError } from '../../lib/api-client';
import { formatDateRange, formatRoute } from '../../lib/format';

const STATUS_CLS: Record<FlightOfferStatusEnum, string> = {
  ACTIVE: 'bg-emerald-900/40 text-emerald-300 border-emerald-800',
  FULL: 'bg-amber-900/40 text-amber-300 border-amber-800',
  CANCELLED_BY_PILOT: 'bg-rose-900/40 text-rose-300 border-rose-800',
  CANCELLED_BY_ADMIN: 'bg-rose-900/40 text-rose-300 border-rose-800',
  EXPIRED: 'bg-slate-800 text-slate-400 border-slate-700',
};

// Inline-Heroicons-Style — keine externe Icon-Lib im Projekt
function EyeIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
      <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function EyeSlashIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
  );
}

export function AdminFlightsPage() {
  const [status, setStatus] = useState<FlightOfferStatusEnum | ''>('');
  const [showHidden, setShowHidden] = useState(false);
  const list = useAdminFlights(status || undefined, showHidden);
  const deactivate = useDeactivateFlight();
  const hide = useHideFlight();
  const unhide = useUnhideFlight();
  const [reasonForId, setReasonForId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  return (
    <div className="px-4 py-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Flüge (Admin)</h1>
        <button
          onClick={() => setShowHidden((v) => !v)}
          title={
            showHidden
              ? 'Ausgeblendete sind sichtbar – Klick zum Verstecken'
              : 'Ausgeblendete einblenden'
          }
          aria-pressed={showHidden}
          className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors ${
            showHidden
              ? 'border-brand-700 bg-brand-900/40 text-brand-200'
              : 'border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700'
          }`}
        >
          {showHidden ? (
            <EyeIcon className="h-4 w-4" />
          ) : (
            <EyeSlashIcon className="h-4 w-4" />
          )}
          {showHidden ? 'Ausgeblendete sichtbar' : 'Ausgeblendete'}
        </button>
      </div>

      <select
        value={status}
        onChange={(e) =>
          setStatus(e.target.value as FlightOfferStatusEnum | '')
        }
        className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
      >
        <option value="">Alle Status</option>
        {Object.values(FlightOfferStatus).map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>

      {list.isLoading ? (
        <p className="py-12 text-center text-sm text-slate-400">Lädt …</p>
      ) : list.data && list.data.length > 0 ? (
        <ul className="space-y-2">
          {list.data.map((o) => {
            const reg =
              o.aircraft?.registration ??
              o.aircraftRegistrationText ??
              '—';
            const canDeactivate = o.status === 'ACTIVE' || o.status === 'FULL';
            const isHidden = !!o.hiddenAt;
            return (
              <li
                key={o.id}
                className={`rounded-xl border p-3 ${
                  isHidden
                    ? 'border-slate-800 bg-slate-900/30 opacity-70'
                    : 'border-slate-800 bg-slate-900/60'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link
                      to={`/app/flights/${o.id}`}
                      className="text-sm font-medium hover:underline"
                    >
                      {formatRoute(o.fromLocation, o.toLocation, o.roundTrip)}
                    </Link>
                    <p className="text-xs text-slate-400">
                      {formatDateRange(o.startAt, o.endAt)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {reg}
                      {o.pilot
                        ? ` · ${o.pilot.firstName} ${o.pilot.lastName}`
                        : ''}
                      {' · '}
                      {o.confirmedSeats}/{o.freeSeats} belegt
                    </p>
                    {o.adminCancelReason && (
                      <p className="mt-1 text-xs text-rose-300">
                        Admin: {o.adminCancelReason}
                      </p>
                    )}
                    {isHidden && (
                      <p className="mt-1 text-xs text-slate-400 italic">
                        Ausgeblendet
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[10px] ${STATUS_CLS[o.status]}`}
                    >
                      {o.status}
                    </span>
                    <button
                      onClick={() =>
                        isHidden
                          ? unhide.mutate(o.id)
                          : hide.mutate(o.id)
                      }
                      disabled={hide.isPending || unhide.isPending}
                      title={isHidden ? 'Wieder einblenden' : 'Ausblenden'}
                      aria-label={
                        isHidden ? 'Wieder einblenden' : 'Ausblenden'
                      }
                      className="rounded-md border border-slate-700 bg-slate-800 p-1.5 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
                    >
                      {isHidden ? (
                        <EyeSlashIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {canDeactivate && reasonForId !== o.id && (
                  <button
                    onClick={() => {
                      setReasonForId(o.id);
                      setReason('');
                    }}
                    className="mt-2 text-xs text-rose-300 hover:underline"
                  >
                    Deaktivieren …
                  </button>
                )}
                {reasonForId === o.id && (
                  <div className="mt-3 space-y-2 rounded-lg border border-rose-900 bg-rose-950/30 p-2">
                    <input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Grund (Pflicht)"
                      className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          reason.trim() &&
                          deactivate.mutate(
                            { id: o.id, reason: reason.trim() },
                            { onSuccess: () => setReasonForId(null) },
                          )
                        }
                        disabled={!reason.trim() || deactivate.isPending}
                        className="flex-1 rounded bg-rose-700 px-3 py-1.5 text-xs hover:bg-rose-600 disabled:opacity-60"
                      >
                        Bestätigen
                      </button>
                      <button
                        onClick={() => setReasonForId(null)}
                        className="rounded border border-slate-700 px-3 py-1.5 text-xs"
                      >
                        Abbrechen
                      </button>
                    </div>
                    {deactivate.error instanceof ApiError && (
                      <p className="text-xs text-rose-400">
                        {deactivate.error.message}
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="py-12 text-center text-sm text-slate-400">Keine Flüge.</p>
      )}
    </div>
  );
}
